<template>
  <div class="product-admin-workspace">
    <header class="workspace-header">
      <div>
        <div class="workspace-eyebrow">Katalog &amp; Verfügbarkeit</div>
        <h2 class="workspace-title">Produkte verwalten</h2>
        <p class="workspace-subtitle">Produkte, Downloads, Varianten und Studio-Freigaben an einem Ort.</p>
      </div>
      <v-btn color="primary" depressed rounded class="create-product-btn" @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        Neues Produkt
      </v-btn>
    </header>

    <section class="kpi-grid" aria-label="Produktübersicht">
      <v-card v-for="kpi in productKpis" :key="kpi.label" dark flat class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-copy">
            <span class="kpi-label">{{ kpi.label }}</span>
            <strong class="kpi-value">{{ kpi.value }}</strong>
            <span class="kpi-helper">{{ kpi.helper }}</span>
          </div>
          <div class="kpi-icon" :class="`kpi-icon--${kpi.tone}`" aria-hidden="true">
            <v-icon>{{ kpi.icon }}</v-icon>
          </div>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="filter-panel">
      <div class="filter-grid">
        <v-text-field
          v-model="searchQuery"
          label="Produkte durchsuchen"
          placeholder="Titel, Beschreibung oder Studio"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details />
        <v-select v-model="typeFilter" :items="typeFilterOptions" label="Typ" outlined dense hide-details />
        <v-select v-model="activeFilter" :items="activeFilterOptions" label="Status" outlined dense hide-details />
        <v-select v-model="stockFilter" :items="stockFilterOptions" label="Bestand" outlined dense hide-details />
        <v-select v-model="studioFilter" :items="studioFilterOptions" label="Studio-Freigabe" outlined dense hide-details />
        <v-btn v-if="hasActiveFilters" text color="primary" class="reset-filter-btn" @click="resetFilters">
          <v-icon left small>mdi-filter-remove-outline</v-icon>
          Filter zurücksetzen
        </v-btn>
      </div>
      <div class="filter-summary" role="status" aria-live="polite">
        <v-icon small color="grey lighten-1" class="mr-2">mdi-format-list-bulleted</v-icon>
        <span>{{ filteredProducts.length }} von {{ products.length }} Produkten</span>
      </div>
    </v-card>

    <v-alert v-if="workspaceError" type="error" outlined dismissible class="workspace-alert" @input="workspaceError = ''">
      {{ workspaceError }}
    </v-alert>

    <div v-if="loading" class="workspace-state" role="status" aria-live="polite">
      <v-progress-circular indeterminate color="primary" size="44"></v-progress-circular>
      <div class="workspace-state-title">Produkte werden geladen</div>
      <div class="workspace-state-copy">Katalog und Bestände werden aktualisiert.</div>
    </div>

    <div v-else-if="products.length === 0" class="workspace-state">
      <v-icon size="64" color="grey darken-1">mdi-package-variant-remove</v-icon>
      <div class="workspace-state-title">Noch keine Produkte erfasst</div>
      <div class="workspace-state-copy">Lege das erste Produkt an, um den Shop zu befüllen.</div>
      <v-btn color="primary" depressed rounded class="mt-5" @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        Erstes Produkt erstellen
      </v-btn>
    </div>

    <div v-else-if="filteredProducts.length === 0" class="workspace-state">
      <v-icon size="64" color="grey darken-1">mdi-filter-variant-remove</v-icon>
      <div class="workspace-state-title">Keine passenden Produkte</div>
      <div class="workspace-state-copy">Passe Suche oder Filter an, um wieder Ergebnisse zu sehen.</div>
      <v-btn outlined color="primary" class="mt-5" @click="resetFilters">Filter zurücksetzen</v-btn>
    </div>

    <template v-else>
      <v-card v-if="$vuetify.breakpoint.mdAndUp" dark flat class="product-table-card">
        <v-data-table
          dark
          :headers="headers"
          :items="filteredProducts"
          :items-per-page="25"
          item-key="_id"
          class="product-table"
          no-data-text="Keine Produkte gefunden"
          :footer-props="{ 'items-per-page-text': 'Produkte pro Seite' }">
          <template v-slot:item.image="{ item }">
            <v-avatar size="44" tile rounded class="my-2 product-thumb">
              <admin-product-thumb :product="item" />
            </v-avatar>
          </template>
          <template v-slot:item.title="{ item }">
            <div class="table-product-title">{{ item.title }}</div>
            <div v-if="item.description" class="table-product-description">{{ item.description }}</div>
          </template>
          <template v-slot:item.productType="{ item }">
            <v-chip small :color="item.productType === 'digital' ? 'primary' : 'blue-grey darken-1'" dark>
              <v-icon left small>{{ item.productType === "digital" ? "mdi-download" : "mdi-package-variant" }}</v-icon>
              {{ item.productType === "digital" ? "Download" : "Physisch" }}
            </v-chip>
          </template>
          <template v-slot:item.minMax="{ item }">
            <span v-if="item.productType === 'digital'" class="table-secondary-text">–</span>
            <span v-else class="table-secondary-text">{{ item.minQuantity }} – {{ item.maxQuantity }}</span>
          </template>
          <template v-slot:item.stock="{ item }">
            <v-chip small outlined :color="stockColor(item)">
              <v-icon left small>{{ stockIcon(item) }}</v-icon>
              {{ getProductTotalStockInfo(item).text }}
            </v-chip>
          </template>
          <template v-slot:item.images="{ item }">
            <span class="table-secondary-text">
              <v-icon small color="grey lighten-1" class="mr-1">mdi-image-multiple</v-icon>
              {{ item.images ? item.images.length : 0 }}
            </span>
          </template>
          <template v-slot:item.studios="{ item }">
            <span class="table-secondary-text">{{ studioAvailabilityLabel(item) }}</span>
          </template>
          <template v-slot:item.active="{ item }">
            <v-chip small :color="isProductActive(item) ? 'green darken-2' : 'grey darken-1'" dark>
              <v-icon left small>{{ isProductActive(item) ? "mdi-check-circle" : "mdi-pause-circle" }}</v-icon>
              {{ isProductActive(item) ? "Aktiv" : "Inaktiv" }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="table-actions">
              <v-tooltip v-if="item.productType === 'digital' && item.file" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    class="icon-action"
                    :aria-label="`Datei von ${item.title} herunterladen`"
                    @click="downloadFile(item)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Datei herunterladen</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon class="icon-action" :aria-label="`${item.title} bearbeiten`" @click="openEdit(item)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Produkt bearbeiten</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="error"
                    class="icon-action"
                    :aria-label="`${item.title} löschen`"
                    @click="confirmDelete(item)">
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Produkt löschen</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div v-else class="mobile-product-list" aria-label="Produkte">
        <article v-for="item in filteredProducts" :key="item._id" class="mobile-product-card">
          <div class="mobile-card-header">
            <div class="mobile-product-thumb">
              <admin-product-thumb :product="item" />
            </div>
            <div class="mobile-card-heading">
              <h3>{{ item.title }}</h3>
              <div class="mobile-card-chips">
                <v-chip x-small :color="item.productType === 'digital' ? 'primary' : 'blue-grey darken-1'" dark>
                  {{ item.productType === "digital" ? "Download" : "Physisch" }}
                </v-chip>
                <v-chip x-small :color="isProductActive(item) ? 'green darken-2' : 'grey darken-1'" dark>
                  {{ isProductActive(item) ? "Aktiv" : "Inaktiv" }}
                </v-chip>
              </div>
            </div>
          </div>

          <dl class="mobile-product-meta">
            <div>
              <dt>Bestand</dt>
              <dd :class="stockTextClass(item)">{{ getProductTotalStockInfo(item).text }}</dd>
            </div>
            <div>
              <dt>Studios</dt>
              <dd>{{ studioAvailabilityLabel(item) }}</dd>
            </div>
            <div>
              <dt>Bilder</dt>
              <dd>{{ item.images ? item.images.length : 0 }}</dd>
            </div>
          </dl>

          <div class="mobile-card-actions">
            <v-tooltip v-if="item.productType === 'digital' && item.file" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  class="icon-action"
                  :aria-label="`Datei von ${item.title} herunterladen`"
                  @click="downloadFile(item)">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Datei herunterladen</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-btn text color="primary" class="mobile-edit-btn" @click="openEdit(item)">
              <v-icon left>mdi-pencil</v-icon>
              Bearbeiten
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  color="error"
                  class="icon-action"
                  :aria-label="`${item.title} löschen`"
                  @click="confirmDelete(item)">
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Produkt löschen</span>
            </v-tooltip>
          </div>
        </article>
      </div>
    </template>

    <!-- Create/Edit Dialog -->
    <v-dialog
      v-model="dialogOpen"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="820"
      persistent
      scrollable
      content-class="product-admin-dialog"
      aria-labelledby="product-dialog-title">
      <v-card dark class="product-dialog-card">
        <v-card-title class="product-dialog-header">
          <div class="dialog-title-copy">
            <div class="dialog-eyebrow">{{ isEditMode ? "Produkt aktualisieren" : "Katalog erweitern" }}</div>
            <h2 id="product-dialog-title">{{ isEditMode ? "Produkt bearbeiten" : "Neues Produkt" }}</h2>
            <p>
              {{
                isEditMode
                  ? "Passe Inhalte, Freigaben und Varianten strukturiert an."
                  : "Führe das Produkt in fünf klaren Abschnitten durch die Erfassung."
              }}
            </p>
          </div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                class="icon-action dialog-close-btn"
                aria-label="Produktdialog schliessen"
                :disabled="saving"
                @click="requestCloseDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Dialog schliessen</span>
          </v-tooltip>
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text ref="dialogBody" class="product-dialog-body">
          <ol class="form-section-map" aria-label="Formularabschnitte">
            <li v-for="section in formSections" :key="section.number">
              <span class="map-number">{{ section.number }}</span>
              <span>{{ section.label }}</span>
            </li>
          </ol>

          <v-alert v-if="formError" type="error" outlined class="form-error" role="alert">
            {{ formError }}
          </v-alert>

          <v-form ref="productForm" v-model="formValid">
            <section class="product-form-section" aria-labelledby="section-basic-title">
              <div class="section-heading">
                <span class="section-number">1</span>
                <div>
                  <h3 id="section-basic-title">Grunddaten</h3>
                  <p>Definiere Produkttyp, Inhalt und Sichtbarkeit im Katalog.</p>
                </div>
              </div>
              <div class="section-body">
                <div class="field-label">Produkt-Typ</div>
                <v-btn-toggle v-model="form.productType" mandatory color="primary" dark class="product-type-toggle" aria-label="Produkt-Typ">
                  <v-btn value="physical">
                    <v-icon left small>mdi-package-variant</v-icon>
                    Physisch
                  </v-btn>
                  <v-btn value="digital">
                    <v-icon left small>mdi-download</v-icon>
                    Download
                  </v-btn>
                </v-btn-toggle>

                <v-text-field
                  v-model="form.title"
                  label="Titel *"
                  outlined
                  dense
                  dark
                  class="mt-5"
                  :rules="[(v) => !!(v && v.trim()) || 'Bitte einen Titel eingeben']" />
                <v-textarea
                  v-model="form.description"
                  label="Beschreibung"
                  hint="Kurze, konkrete Beschreibung für die Produktkarte und Detailansicht."
                  persistent-hint
                  outlined
                  dense
                  dark
                  rows="4" />

                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="form.sortOrder"
                      label="Sortierung"
                      type="number"
                      outlined
                      dense
                      dark
                      hint="Kleinere Werte erscheinen zuerst."
                      persistent-hint />
                  </v-col>
                  <v-col cols="12" sm="6" class="active-switch-column">
                    <v-switch v-model="form.active" label="Im Shop aktiv" color="primary" inset />
                    <span class="field-helper">Inaktive Produkte bleiben in der Verwaltung erhalten.</span>
                  </v-col>
                </v-row>
              </div>
            </section>

            <section class="product-form-section" aria-labelledby="section-availability-title">
              <div class="section-heading">
                <span class="section-number">2</span>
                <div>
                  <h3 id="section-availability-title">Verfügbarkeit &amp; Bestellregeln</h3>
                  <p>Lege Studio-Freigaben und zulässige Bestellmengen fest.</p>
                </div>
              </div>
              <div class="section-body">
                <v-select
                  v-model="form.availableInStudios"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Verfügbar in Studios"
                  outlined
                  dense
                  dark
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="Keine Auswahl bedeutet: in allen Studios verfügbar."
                  persistent-hint />

                <v-row v-if="form.productType !== 'digital'" dense class="mt-3">
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="form.minQuantity"
                      label="Minimale Bestellmenge"
                      type="number"
                      outlined
                      dense
                      dark
                      min="1"
                      :rules="[(v) => Number(v) >= 1 || 'Mindestens 1']" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="form.maxQuantity"
                      label="Maximale Bestellmenge"
                      type="number"
                      outlined
                      dense
                      dark
                      min="1"
                      :rules="[(v) => Number(v) >= Number(form.minQuantity) || 'Muss mindestens der Minimalmenge entsprechen']" />
                  </v-col>
                </v-row>
                <v-alert v-else text color="primary" icon="mdi-information-outline" class="section-hint">
                  Downloads werden pro Bestellung einmal ausgewählt; Mengenregeln sind dafür nicht nötig.
                </v-alert>
              </div>
            </section>

            <section class="product-form-section" aria-labelledby="section-media-title">
              <div class="section-heading">
                <span class="section-number">3</span>
                <div>
                  <h3 id="section-media-title">Produktbilder</h3>
                  <p>Gute Vorschaubilder machen Produkte im Shop schneller erfassbar.</p>
                </div>
              </div>
              <div class="section-body">
                <div v-if="isEditMode && existingImages.length > 0">
                  <div class="field-label">Bestehende Bilder ({{ existingImages.length }})</div>
                  <div class="existing-images">
                    <div v-for="img in existingImages" :key="img.fileId" class="existing-img-wrap">
                      <img
                        v-if="imageUrls[img.fileId]"
                        :src="imageUrls[img.fileId]"
                        :alt="img.originalName || `Produktbild von ${form.title}`"
                        class="existing-img" />
                      <div v-else class="existing-img-loading" role="status" aria-label="Bild wird geladen">
                        <v-progress-circular indeterminate size="20"></v-progress-circular>
                      </div>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            class="icon-action remove-img-btn"
                            color="error"
                            :aria-label="`${img.originalName || 'Bild'} entfernen`"
                            @click="markImageForRemoval(img.fileId)">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <span>Bild entfernen</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>

                <v-file-input
                  v-model="form.newImages"
                  multiple
                  accept="image/*"
                  label="Neue Bilder hochladen"
                  outlined
                  dense
                  dark
                  show-size
                  counter
                  prepend-icon=""
                  prepend-inner-icon="mdi-image-plus"
                  hint="Mehrere Bilder möglich, maximal 10 MB pro Bild."
                  persistent-hint
                  class="mt-4" />
              </div>
            </section>

            <section class="product-form-section" aria-labelledby="section-variants-title">
              <div class="section-heading section-heading--with-action">
                <span class="section-number">4</span>
                <div>
                  <h3 id="section-variants-title">Optionen &amp; Varianten</h3>
                  <p>Erstelle Auswahlwerte wie Farbe oder Grösse, bevor du Bestand und Dateien zuordnest.</p>
                </div>
                <v-btn color="primary" outlined class="section-action" @click="addOption">
                  <v-icon left small>mdi-plus</v-icon>
                  Option hinzufügen
                </v-btn>
              </div>
              <div class="section-body">
                <div v-if="form.options && form.options.length > 0" class="options-container">
                  <v-card v-for="(opt, optIdx) in form.options" :key="optIdx" class="option-card" dark flat>
                    <div class="option-card-header">
                      <span class="option-index">Option {{ optIdx + 1 }}</span>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            color="error"
                            class="icon-action"
                            :aria-label="`Option ${optIdx + 1} entfernen`"
                            @click="removeOption(optIdx)">
                            <v-icon>mdi-delete-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Option entfernen</span>
                      </v-tooltip>
                    </div>
                    <v-text-field
                      v-model="opt.name"
                      label="Name der Option (z.B. Farbe, Grösse)"
                      outlined
                      dense
                      dark
                      :rules="[(v) => !!(v && v.trim()) || 'Bitte einen Optionsnamen eingeben']" />

                    <div class="choices-container">
                      <div class="field-label">Auswahlwerte</div>
                      <div v-for="(choice, choiceIdx) in opt.choices" :key="choiceIdx" class="choice-row">
                        <v-text-field
                          v-model="choice.value"
                          label="Wert *"
                          placeholder="z.B. Weiss oder XL"
                          outlined
                          dense
                          dark
                          class="choice-value"
                          :rules="[(v) => !!(v && v.trim()) || 'Bitte einen Wert eingeben']" />

                        <v-select
                          v-model="choice.imageFileId"
                          :items="existingImages"
                          item-text="originalName"
                          item-value="fileId"
                          label="Bild verknüpfen (optional)"
                          outlined
                          dense
                          dark
                          clearable
                          class="choice-image-select">
                          <template v-slot:item="{ item }">
                            <v-avatar size="24" tile class="mr-2 rounded">
                              <img v-if="imageUrls[item.fileId]" :src="imageUrls[item.fileId]" :alt="item.originalName || 'Produktbild'" />
                              <v-icon v-else small color="grey">mdi-image</v-icon>
                            </v-avatar>
                            <span class="text-truncate text-caption image-option-name">{{ item.originalName }}</span>
                          </template>
                          <template v-slot:selection="{ item }">
                            <v-avatar v-if="imageUrls[item.fileId]" size="20" tile class="mr-2 rounded">
                              <img :src="imageUrls[item.fileId]" :alt="item.originalName || 'Produktbild'" />
                            </v-avatar>
                            <span class="text-truncate text-caption image-selection-name">{{ item.originalName }}</span>
                          </template>
                        </v-select>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              color="error"
                              class="icon-action choice-remove-btn"
                              :aria-label="`Wert ${choiceIdx + 1} entfernen`"
                              @click="removeChoice(optIdx, choiceIdx)">
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Wert entfernen</span>
                        </v-tooltip>
                      </div>

                      <v-btn text color="primary" class="add-choice-btn" @click="addChoice(optIdx)">
                        <v-icon left small>mdi-plus</v-icon>
                        Wert hinzufügen
                      </v-btn>
                    </div>
                  </v-card>
                </div>
                <div v-else class="variants-empty-state">
                  <v-icon size="42" color="grey darken-1">mdi-tune-variant</v-icon>
                  <strong>Keine Varianten definiert</strong>
                  <span>Das Produkt kann ohne zusätzliche Auswahl direkt bestellt werden.</span>
                </div>
              </div>
            </section>

            <section class="product-form-section" aria-labelledby="section-fulfillment-title">
              <div class="section-heading">
                <span class="section-number">5</span>
                <div>
                  <h3 id="section-fulfillment-title">Bestand &amp; Auslieferung</h3>
                  <p>Pflege den Bestand und – bei Downloads – die auszuliefernden PDF-Dateien.</p>
                </div>
              </div>
              <div class="section-body">
                <div v-if="!form.options || form.options.length === 0" class="stock-section">
                  <v-text-field
                    v-model.number="form.stock"
                    label="Lagerbestand"
                    type="number"
                    min="0"
                    outlined
                    dense
                    dark
                    clearable
                    hint="Leer lassen für unbegrenzten Lagerbestand."
                    persistent-hint />
                </div>
                <div v-else class="variant-stock-section">
                  <div class="subsection-heading">
                    <div>
                      <strong>Lagerbestand pro Variante</strong>
                      <span>Leere Felder gelten als unbegrenzt.</span>
                    </div>
                    <v-chip small outlined color="primary">{{ variantCombinations.length }} Kombinationen</v-chip>
                  </div>
                  <v-alert v-if="variantCombinations.length === 0" text color="primary" icon="mdi-information-outline">
                    Ergänze in Abschnitt 4 einen Optionsnamen und mindestens einen Auswahlwert.
                  </v-alert>
                  <v-row v-else dense>
                    <v-col v-for="comb in variantCombinations" :key="comb.signature" cols="12" sm="6">
                      <v-text-field
                        v-model.number="form.variantStock[comb.signature]"
                        :label="comb.label"
                        type="number"
                        min="0"
                        outlined
                        dense
                        dark
                        clearable
                        hint="Unbegrenzt, falls leer"
                        persistent-hint />
                    </v-col>
                  </v-row>
                </div>

                <div v-if="form.productType === 'digital'" class="digital-file-section">
                  <div class="subsection-heading">
                    <div>
                      <strong>{{ form.options && form.options.length > 0 ? "PDF pro Variante" : "Download-Datei" }}</strong>
                      <span>PDF, maximal 25 MB pro Datei.</span>
                    </div>
                    <v-icon color="primary">mdi-file-pdf-box</v-icon>
                  </div>

                  <div v-if="!form.options || form.options.length === 0">
                    <div v-if="isEditMode && existingDigitalFile && !removeDigitalFile" class="existing-file-card mb-3">
                      <v-icon color="primary" class="mr-3">mdi-file-pdf-box</v-icon>
                      <div class="existing-file-copy">
                        <div class="font-weight-medium">{{ existingDigitalFile.originalName }}</div>
                        <div class="caption grey--text">
                          {{ formatFileSize(existingDigitalFile.size) }} – hochgeladen
                          {{ $moment(existingDigitalFile.uploadedAt).format("DD.MM.YYYY") }}
                        </div>
                      </div>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            color="error"
                            class="icon-action"
                            aria-label="Download-Datei entfernen"
                            @click="removeDigitalFile = true">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <span>Datei entfernen</span>
                      </v-tooltip>
                    </div>

                    <v-file-input
                      v-model="form.digitalFile"
                      accept="application/pdf,.pdf"
                      :label="existingDigitalFile && !removeDigitalFile ? 'Datei ersetzen' : 'PDF hochladen *'"
                      outlined
                      dense
                      dark
                      show-size
                      prepend-icon=""
                      prepend-inner-icon="mdi-cloud-upload"
                      hint="Die Datei steht nach der Bestellung zum Download bereit."
                      persistent-hint />
                  </div>

                  <div v-else>
                    <v-alert v-if="variantCombinations.length === 0" text color="primary" icon="mdi-information-outline">
                      Definiere zuerst gültige Varianten in Abschnitt 4.
                    </v-alert>
                    <v-row v-else dense>
                      <v-col v-for="comb in variantCombinations" :key="comb.signature" cols="12" sm="6" class="mb-3">
                        <div class="variant-file-label">{{ comb.label }}</div>
                        <div
                          v-if="isEditMode && form.variantFiles[comb.signature] && !variantFilesToRemove.includes(comb.signature)"
                          class="existing-file-card mb-2">
                          <v-icon color="primary" class="mr-2">mdi-file-pdf-box</v-icon>
                          <div class="existing-file-copy">
                            <div class="font-weight-medium text-truncate variant-file-name">
                              {{ form.variantFiles[comb.signature].originalName }}
                            </div>
                          </div>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                color="error"
                                class="icon-action"
                                :aria-label="`Datei für ${comb.label} entfernen`"
                                @click="markVariantFileForRemoval(comb.signature)">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </template>
                            <span>Datei entfernen</span>
                          </v-tooltip>
                        </div>
                        <v-file-input
                          v-else
                          v-model="form.newVariantFiles[comb.signature]"
                          accept="application/pdf,.pdf"
                          label="PDF hochladen"
                          outlined
                          dense
                          dark
                          show-size
                          prepend-icon=""
                          prepend-inner-icon="mdi-cloud-upload"
                          hide-details />
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </div>
            </section>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="product-dialog-footer">
          <div class="footer-validation" aria-live="polite">
            <v-icon small :color="formValid ? 'success' : 'grey lighten-1'">
              {{ formValid ? "mdi-check-circle" : "mdi-information-outline" }}
            </v-icon>
            <span>{{ formValid ? "Pflichtfelder sind ausgefüllt" : "Pflichtfelder bitte vollständig ausfüllen" }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-btn text class="footer-action" :disabled="saving" @click="requestCloseDialog">Abbrechen</v-btn>
          <v-btn color="primary" depressed class="footer-action save-product-btn" :loading="saving" :disabled="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ isEditMode ? "Änderungen speichern" : "Produkt erstellen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialogOpen" max-width="480" persistent aria-labelledby="delete-product-title">
      <v-card dark class="delete-dialog-card">
        <v-card-title class="delete-dialog-title">
          <div class="delete-icon" aria-hidden="true">
            <v-icon color="error">mdi-delete-alert-outline</v-icon>
          </div>
          <div>
            <h2 id="delete-product-title">Produkt löschen?</h2>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
          </div>
        </v-card-title>
        <v-card-text>
          <p v-if="productPendingDelete" class="delete-product-name">„{{ productPendingDelete.title }}“</p>
          <p>Das Produkt wird aus Verwaltung und Shop entfernt. Prüfe vor dem Löschen, ob es noch benötigt wird.</p>
          <v-alert v-if="deleteError" type="error" outlined dense role="alert">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions class="delete-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text :disabled="deletingProduct" @click="closeDeleteDialog">Abbrechen</v-btn>
          <v-btn color="error" depressed :loading="deletingProduct" :disabled="deletingProduct" @click="executeDelete">
            <v-icon left>mdi-delete</v-icon>
            Endgültig löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="discardProductDialogOpen" max-width="460" persistent>
      <v-card dark>
        <v-card-title>
          <v-icon left color="warning">mdi-content-save-alert-outline</v-icon>
          Änderungen verwerfen?
        </v-card-title>
        <v-card-text>Deine noch nicht gespeicherten Eingaben an diesem Produkt gehen verloren.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="discardProductDialogOpen = false">Weiter bearbeiten</v-btn>
          <v-btn color="warning" depressed @click="confirmDiscardProduct">Änderungen verwerfen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

const AdminProductThumb = {
  name: "AdminProductThumb",
  props: { product: { type: Object, required: true } },
  data() {
    return {
      url: null,
      failed: false,
      loading: false,
      requestId: 0,
    }
  },
  computed: {
    imageSignature() {
      const productId = this.product && this.product._id ? String(this.product._id) : ""
      const firstImage = this.product && this.product.images && this.product.images[0]
      const fileId = firstImage && firstImage.fileId ? String(firstImage.fileId) : ""
      return `${productId}:${fileId}`
    },
  },
  watch: {
    imageSignature: {
      immediate: true,
      handler() {
        this.loadImage()
      },
    },
  },
  methods: {
    revokeCurrentUrl() {
      if (!this.url) return
      URL.revokeObjectURL(this.url)
      this.url = null
    },
    async loadImage() {
      const currentRequest = ++this.requestId
      this.revokeCurrentUrl()
      this.failed = false

      const productId = this.product && this.product._id
      const firstImage = this.product && this.product.images && this.product.images[0]
      const fileId = firstImage && firstImage.fileId
      if (!productId || !fileId) {
        this.loading = false
        return
      }

      this.loading = true
      try {
        const url = await api.marketingShop.loadProductImage(productId, fileId)
        if (currentRequest !== this.requestId) {
          if (url) URL.revokeObjectURL(url)
          return
        }
        this.url = url
      } catch (error) {
        if (currentRequest === this.requestId) this.failed = true
      } finally {
        if (currentRequest === this.requestId) this.loading = false
      }
    },
    handleImageError() {
      this.failed = true
      this.revokeCurrentUrl()
    },
  },
  beforeDestroy() {
    this.requestId += 1
    this.revokeCurrentUrl()
  },
  render(h) {
    const title = this.product && this.product.title ? this.product.title : "Produkt"
    const fillStyle = { width: "100%", height: "100%" }
    const stateStyle = {
      ...fillStyle,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255,255,255,0.05)",
    }

    let content
    if (this.loading) {
      content = h(
        "div",
        {
          style: stateStyle,
          attrs: { role: "status", "aria-label": `Vorschaubild für ${title} wird geladen` },
        },
        [h("v-progress-circular", { props: { indeterminate: true, size: 18, width: 2, color: "grey" } })],
      )
    } else if (this.url && !this.failed) {
      content = h("img", {
        style: { ...fillStyle, display: "block", objectFit: "cover" },
        attrs: { src: this.url, alt: title },
        on: { error: this.handleImageError },
      })
    } else {
      content = h(
        "div",
        {
          style: stateStyle,
          attrs: { role: "img", "aria-label": `Kein Vorschaubild für ${title}` },
        },
        [h("v-icon", { props: { small: true, color: "grey darken-1" } }, ["mdi-image-off"])],
      )
    }

    return h("div", { style: fillStyle }, [content])
  },
}

export default {
  name: "ProductAdmin",
  components: { AdminProductThumb },
  props: {
    products: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    studioOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      searchQuery: "",
      typeFilter: "all",
      activeFilter: "all",
      stockFilter: "all",
      studioFilter: "all",
      typeFilterOptions: [
        { text: "Alle Typen", value: "all" },
        { text: "Physisch", value: "physical" },
        { text: "Download", value: "digital" },
      ],
      activeFilterOptions: [
        { text: "Alle Status", value: "all" },
        { text: "Aktiv", value: "active" },
        { text: "Inaktiv", value: "inactive" },
      ],
      stockFilterOptions: [
        { text: "Alle Bestände", value: "all" },
        { text: "Auf Lager", value: "available" },
        { text: "Knapp (bis 10)", value: "low" },
        { text: "Ausverkauft", value: "out" },
        { text: "Unbegrenzt", value: "unlimited" },
      ],
      formSections: [
        { number: 1, label: "Grunddaten" },
        { number: 2, label: "Verfügbarkeit" },
        { number: 3, label: "Bilder" },
        { number: 4, label: "Varianten" },
        { number: 5, label: "Bestand & Dateien" },
      ],
      workspaceError: "",
      dialogOpen: false,
      formValid: false,
      formError: "",
      saving: false,
      initialFormSnapshot: "",
      discardProductDialogOpen: false,
      editingId: null,
      form: this.getEmptyForm(),
      existingImages: [],
      imagesToRemove: [],
      variantFilesToRemove: [],
      imageUrls: {},
      existingDigitalFile: null,
      removeDigitalFile: false,
      deleteDialogOpen: false,
      productPendingDelete: null,
      deletingProduct: false,
      deleteError: "",
      headers: [
        { text: "Bild", value: "image", sortable: false, width: "60px" },
        { text: "Titel", value: "title", width: "260px" },
        { text: "Typ", value: "productType" },
        { text: "Lagerbestand", value: "stock", sortable: true },
        { text: "Min/Max", value: "minMax", sortable: false },
        { text: "Bilder", value: "images", sortable: false },
        { text: "Sortierung", value: "sortOrder" },
        { text: "Status", value: "active" },
        { text: "Studios", value: "studios", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, align: "end", width: "160px" },
      ],
    }
  },
  computed: {
    studioFilterOptions() {
      return [
        { text: "Alle Studio-Freigaben", value: "all" },
        { text: "Für alle Studios", value: "__global__" },
        ...this.studioOptions.map((studio) => ({ text: studio.name, value: String(studio._id) })),
      ]
    },
    productKpis() {
      const activeCount = this.products.filter((product) => this.isProductActive(product)).length
      const digitalCount = this.products.filter((product) => product.productType === "digital").length
      const attentionCount = this.products.filter((product) => {
        const stock = this.getProductTotalStockInfo(product)
        return stock.type === "limited" && stock.value <= 10
      }).length

      return [
        {
          label: "Produkte gesamt",
          value: this.products.length,
          helper: "im Katalog",
          icon: "mdi-package-variant-closed",
          tone: "neutral",
        },
        {
          label: "Aktiv im Shop",
          value: activeCount,
          helper: `${this.products.length - activeCount} inaktiv`,
          icon: "mdi-store-check-outline",
          tone: "success",
        },
        {
          label: "Downloads",
          value: digitalCount,
          helper: `${this.products.length - digitalCount} physisch`,
          icon: "mdi-cloud-download-outline",
          tone: "info",
        },
        {
          label: "Bestand prüfen",
          value: attentionCount,
          helper: "knapp oder ausverkauft",
          icon: "mdi-warehouse",
          tone: attentionCount > 0 ? "warning" : "success",
        },
      ]
    },
    filteredProducts() {
      const query = (this.searchQuery || "").trim().toLowerCase()

      return this.products.filter((product) => {
        const productType = product.productType || "physical"
        if (this.typeFilter !== "all" && productType !== this.typeFilter) return false

        const isActive = this.isProductActive(product)
        if (this.activeFilter === "active" && !isActive) return false
        if (this.activeFilter === "inactive" && isActive) return false

        const stock = this.getProductTotalStockInfo(product)
        if (this.stockFilter === "unlimited" && stock.type !== "unlimited") return false
        if (this.stockFilter === "available" && (stock.type !== "limited" || stock.value <= 0)) return false
        if (this.stockFilter === "low" && (stock.type !== "limited" || stock.value <= 0 || stock.value > 10)) return false
        if (this.stockFilter === "out" && (stock.type !== "limited" || stock.value > 0)) return false

        const studioIds = this.getProductStudioIds(product)
        if (this.studioFilter === "__global__" && studioIds.length > 0) return false
        if (this.studioFilter !== "all" && this.studioFilter !== "__global__" && !studioIds.includes(String(this.studioFilter))) {
          return false
        }

        if (query) {
          const haystack = [
            product.title,
            product.description,
            productType === "digital" ? "Download digital" : "Physisch",
            ...this.getProductStudioNames(product),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
          if (!haystack.includes(query)) return false
        }

        return true
      })
    },
    hasActiveFilters() {
      return Boolean(
        (this.searchQuery && this.searchQuery.trim()) ||
          this.typeFilter !== "all" ||
          this.activeFilter !== "all" ||
          this.stockFilter !== "all" ||
          this.studioFilter !== "all",
      )
    },
    isEditMode() {
      return !!this.editingId
    },
    isFormDirty() {
      return !!(this.dialogOpen && this.initialFormSnapshot && this.buildFormSnapshot() !== this.initialFormSnapshot)
    },
    variantCombinations() {
      if (!this.form || !this.form.options || this.form.options.length === 0) return []

      const validOptions = this.form.options.filter((o) => o.name && o.name.trim() && o.choices && o.choices.some((c) => c.value && c.value.trim()))
      if (validOptions.length === 0) return []

      let results = [[]]
      for (const option of validOptions) {
        const nextResults = []
        const choices = option.choices.filter((c) => c.value && c.value.trim())
        for (const res of results) {
          for (const choice of choices) {
            nextResults.push([...res, { name: option.name.trim(), value: choice.value.trim(), id: choice.id }])
          }
        }
        results = nextResults
      }

      return results.map((comb) => {
        const signature = comb
          .map((c) => c.id)
          .sort()
          .join(",")
        return {
          signature,
          label: comb.map((c) => `${c.name}: ${c.value}`).join(", "),
        }
      })
    },
  },
  watch: {
    variantCombinations: {
      immediate: true,
      handler(combinations) {
        if (!this.form) return
        if (!this.form.variantStock) this.$set(this.form, "variantStock", {})
        if (!this.form.newVariantFiles) this.$set(this.form, "newVariantFiles", {})
        for (const combination of combinations || []) {
          if (!Object.prototype.hasOwnProperty.call(this.form.variantStock, combination.signature)) {
            this.$set(this.form.variantStock, combination.signature, null)
          }
          if (!Object.prototype.hasOwnProperty.call(this.form.newVariantFiles, combination.signature)) {
            this.$set(this.form.newVariantFiles, combination.signature, null)
          }
        }
      },
    },
  },
  methods: {
    resetFilters() {
      this.searchQuery = ""
      this.typeFilter = "all"
      this.activeFilter = "all"
      this.stockFilter = "all"
      this.studioFilter = "all"
    },
    isProductActive(product) {
      return product.active !== false
    },
    getProductStudioIds(product) {
      return (product.availableInStudios || [])
        .map((studio) => {
          if (!studio) return null
          return String(studio._id || studio)
        })
        .filter(Boolean)
    },
    getProductStudioNames(product) {
      return (product.availableInStudios || [])
        .map((studio) => {
          if (!studio) return null
          if (typeof studio === "object" && studio.name) return studio.name
          const studioId = String(studio._id || studio)
          const match = this.studioOptions.find((option) => String(option._id) === studioId)
          return match ? match.name : null
        })
        .filter(Boolean)
    },
    studioAvailabilityLabel(product) {
      const studioIds = this.getProductStudioIds(product)
      if (studioIds.length === 0) return "Alle Studios"

      const names = this.getProductStudioNames(product)
      if (names.length === 0) return `${studioIds.length} Studios`
      if (names.length <= 2) return names.join(", ")
      return `${names.slice(0, 2).join(", ")} +${names.length - 2}`
    },
    stockColor(product) {
      const stock = this.getProductTotalStockInfo(product)
      if (stock.type === "unlimited") return "blue-grey lighten-1"
      if (stock.value <= 0) return "error"
      if (stock.value <= 10) return "orange"
      return "green lighten-1"
    },
    stockIcon(product) {
      const stock = this.getProductTotalStockInfo(product)
      if (stock.type === "unlimited") return "mdi-infinity"
      if (stock.value <= 0) return "mdi-alert-circle-outline"
      if (stock.value <= 10) return "mdi-warehouse"
      return "mdi-check-circle-outline"
    },
    stockTextClass(product) {
      const stock = this.getProductTotalStockInfo(product)
      if (stock.type === "unlimited") return "stock-text stock-text--unlimited"
      if (stock.value <= 0) return "stock-text stock-text--out"
      if (stock.value <= 10) return "stock-text stock-text--low"
      return "stock-text stock-text--available"
    },
    setFormError(message) {
      this.formError = message
      this.$nextTick(() => {
        const body = this.$refs.dialogBody
        const element = body && (body.$el || body)
        if (element) element.scrollTop = 0
      })
    },
    getEmptyForm() {
      return {
        title: "",
        description: "",
        productType: "physical",
        stock: null,
        variantStock: {},
        variantFiles: {},
        newVariantFiles: {},
        minQuantity: 1,
        maxQuantity: 100,
        sortOrder: 0,
        active: true,
        availableInStudios: [],
        newImages: [],
        digitalFile: null,
        options: [],
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B"
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    buildFormSnapshot() {
      const newVariantFiles = Object.entries(this.form.newVariantFiles || {})
        .filter(([, file]) => !!file)
        .map(([signature, file]) => [signature, `${file.name || ""}:${file.size || 0}`])
        .sort((a, b) => a[0].localeCompare(b[0]))
      return JSON.stringify({
        title: this.form.title || "",
        description: this.form.description || "",
        productType: this.form.productType || "physical",
        stock: this.form.stock,
        variantStock: this.form.variantStock || {},
        minQuantity: this.form.minQuantity,
        maxQuantity: this.form.maxQuantity,
        sortOrder: this.form.sortOrder,
        active: this.form.active,
        availableInStudios: (this.form.availableInStudios || []).map(String).sort(),
        options: this.form.options || [],
        newImages: (this.form.newImages || []).map((file) => `${file.name || ""}:${file.size || 0}`),
        digitalFile: this.form.digitalFile ? `${this.form.digitalFile.name || ""}:${this.form.digitalFile.size || 0}` : null,
        newVariantFiles,
        existingImages: (this.existingImages || []).map((image) => image.fileId).sort(),
        imagesToRemove: [...this.imagesToRemove].sort(),
        variantFilesToRemove: [...this.variantFilesToRemove].sort(),
        removeDigitalFile: this.removeDigitalFile,
      })
    },
    captureFormSnapshot() {
      this.initialFormSnapshot = this.buildFormSnapshot()
    },
    openCreate() {
      Object.values(this.imageUrls).forEach((url) => URL.revokeObjectURL(url))
      this.form = this.getEmptyForm()
      this.editingId = null
      this.formError = ""
      this.formValid = false
      this.existingImages = []
      this.imagesToRemove = []
      this.variantFilesToRemove = []
      this.existingDigitalFile = null
      this.removeDigitalFile = false
      this.imageUrls = {}
      this.discardProductDialogOpen = false
      this.dialogOpen = true
      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
        this.captureFormSnapshot()
      })
    },
    async openEdit(product) {
      Object.values(this.imageUrls).forEach((url) => URL.revokeObjectURL(url))
      this.formError = ""
      this.formValid = false
      this.form = {
        title: product.title || "",
        description: product.description || "",
        productType: product.productType || "physical",
        stock: product.stock !== undefined && product.stock !== null ? product.stock : null,
        variantStock: product.variantStock ? { ...product.variantStock } : {},
        variantFiles: product.variantFiles ? { ...product.variantFiles } : {},
        newVariantFiles: {},
        minQuantity: product.minQuantity || 1,
        maxQuantity: product.maxQuantity || 100,
        sortOrder: product.sortOrder || 0,
        active: product.active !== false,
        availableInStudios: (product.availableInStudios || []).map((s) => s._id || s),
        newImages: [],
        digitalFile: null,
        options: (product.options || []).map((o) => ({
          name: o.name || "",
          choices: (o.choices || []).map((c) => ({
            id: c.id || "c_" + Math.random().toString(36).substr(2, 9),
            value: c.value || "",
            imageFileId: c.imageFileId || null,
          })),
        })),
      }
      this.editingId = product._id
      this.existingImages = [...(product.images || [])]
      this.imagesToRemove = []
      this.variantFilesToRemove = []
      this.existingDigitalFile = product.file || null
      this.removeDigitalFile = false
      this.imageUrls = {}
      this.discardProductDialogOpen = false
      this.dialogOpen = true

      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
        this.captureFormSnapshot()
      })

      for (const img of this.existingImages) {
        api.marketingShop
          .loadProductImage(product._id, img.fileId)
          .then((url) => this.$set(this.imageUrls, img.fileId, url))
          .catch(() => {})
      }
    },
    closeDialog() {
      this.dialogOpen = false
      this.discardProductDialogOpen = false
      this.editingId = null
      this.initialFormSnapshot = ""
      this.formError = ""
      this.formValid = false
      Object.values(this.imageUrls).forEach((u) => URL.revokeObjectURL(u))
      this.imageUrls = {}
    },
    requestCloseDialog() {
      if (this.saving) return
      if (this.isFormDirty) {
        this.discardProductDialogOpen = true
        return
      }
      this.closeDialog()
    },
    confirmDiscardProduct() {
      this.discardProductDialogOpen = false
      this.closeDialog()
    },
    addOption() {
      if (!this.form.options) {
        this.$set(this.form, "options", [])
      }
      this.form.options.push({ name: "", choices: [] })
    },
    removeOption(optIdx) {
      this.form.options.splice(optIdx, 1)
    },
    addChoice(optIdx) {
      const choiceId = "c_" + Math.random().toString(36).substr(2, 9)
      this.form.options[optIdx].choices.push({ id: choiceId, value: "", imageFileId: null })
    },
    removeChoice(optIdx, choiceIdx) {
      this.form.options[optIdx].choices.splice(choiceIdx, 1)
    },
    markImageForRemoval(fileId) {
      this.imagesToRemove.push(fileId)
      this.existingImages = this.existingImages.filter((i) => i.fileId !== fileId)
      if (this.imageUrls[fileId]) {
        URL.revokeObjectURL(this.imageUrls[fileId])
        this.$delete(this.imageUrls, fileId)
      }
    },
    async downloadFile(product) {
      this.workspaceError = ""
      try {
        const filename = product.file?.originalName || `${product.title}.pdf`
        await api.marketingShop.downloadProductFile(product._id, filename)
      } catch (e) {
        console.error("Download fehlgeschlagen:", e)
        this.workspaceError = `Die Datei von „${product.title}“ konnte nicht heruntergeladen werden.`
      }
    },
    async save() {
      this.formError = ""
      if (!this.$refs.productForm || !this.$refs.productForm.validate()) {
        this.setFormError("Bitte prüfe die markierten Pflichtfelder, bevor du das Produkt speicherst.")
        return
      }

      if (this.form.productType !== "digital" && this.form.maxQuantity < this.form.minQuantity) {
        this.setFormError("Die maximale Bestellmenge muss grösser oder gleich der minimalen Bestellmenge sein.")
        return
      }

      // Digital-Produkt: Datei(en) müssen vorhanden sein
      if (this.form.productType === "digital") {
        const hasOptions = this.form.options && this.form.options.length > 0
        if (hasOptions) {
          const hasAnyExistingVariantFile = Object.entries(this.form.variantFiles || {}).some(
            ([sig, file]) => file && !this.variantFilesToRemove.includes(sig),
          )
          const hasAnyNewVariantFile = Object.values(this.form.newVariantFiles || {}).some((f) => !!f)
          if (!hasAnyExistingVariantFile && !hasAnyNewVariantFile) {
            this.setFormError("Für ein Download-Produkt mit Varianten muss mindestens eine PDF-Datei hinterlegt sein.")
            return
          }
        } else {
          const hasExisting = this.existingDigitalFile && !this.removeDigitalFile
          const hasNew = !!this.form.digitalFile
          if (!hasExisting && !hasNew) {
            this.setFormError("Bitte lade für das Download-Produkt eine PDF-Datei hoch.")
            return
          }
        }
      }

      this.saving = true
      const wasEditMode = this.isEditMode
      try {
        const fd = new FormData()
        fd.append("title", this.form.title.trim())
        fd.append("description", this.form.description || "")
        fd.append("productType", this.form.productType || "physical")
        fd.append("minQuantity", this.form.minQuantity)
        fd.append("maxQuantity", this.form.maxQuantity)
        fd.append("sortOrder", this.form.sortOrder)
        fd.append("active", this.form.active)
        fd.append("availableInStudios", (this.form.availableInStudios || []).join(","))
        fd.append("stock", this.form.stock !== undefined && this.form.stock !== null && this.form.stock !== "" ? this.form.stock : "")
        if (this.imagesToRemove.length > 0) fd.append("removedImageIds", this.imagesToRemove.join(","))
        if (this.form.newImages && this.form.newImages.length > 0) {
          this.form.newImages.forEach((f) => fd.append("images", f))
        }
        if (this.form.digitalFile) fd.append("digitalFile", this.form.digitalFile)
        if (this.removeDigitalFile && !this.form.digitalFile) fd.append("removeDigitalFile", "true")

        // Varianten-Dateien anhängen
        if (this.form.productType === "digital" && this.form.options && this.form.options.length > 0) {
          Object.entries(this.form.newVariantFiles || {}).forEach(([sig, file]) => {
            if (file) {
              fd.append("variantFile_" + sig, file)
            }
          })
          if (this.variantFilesToRemove.length > 0) {
            fd.append("removedVariantFileSignatures", this.variantFilesToRemove.join(","))
          }
        }

        if (this.form.options && this.form.options.length > 0) {
          const cleanOptions = this.form.options
            .filter((o) => o.name && o.name.trim())
            .map((o) => ({
              name: o.name.trim(),
              choices: (o.choices || [])
                .filter((c) => c.value && c.value.trim())
                .map((c) => ({
                  id: c.id || "c_" + Math.random().toString(36).substr(2, 9),
                  value: c.value.trim(),
                  imageFileId: c.imageFileId || null,
                })),
            }))
          fd.append("options", JSON.stringify(cleanOptions))

          // Nur die aktiven Varianten-Bestände mitsenden
          const finalVarStock = {}
          this.variantCombinations.forEach((comb) => {
            const val = this.form.variantStock[comb.signature]
            if (val !== undefined && val !== null && val !== "") {
              finalVarStock[comb.signature] = val
            }
          })
          fd.append("variantStock", JSON.stringify(finalVarStock))
        } else {
          fd.append("options", JSON.stringify([]))
          fd.append("variantStock", JSON.stringify({}))
        }

        if (this.isEditMode) {
          await api.marketingShop.updateProduct(this.editingId, fd)
        } else {
          await api.marketingShop.createProduct(fd)
        }

        this.closeDialog()
        this.$emit("reload")
        if (this.$toast) {
          this.$toast.success(wasEditMode ? "Produkt erfolgreich aktualisiert" : "Produkt erfolgreich erstellt")
        }
      } catch (e) {
        console.error("Speichern fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern"
        this.setFormError(msg)
      } finally {
        this.saving = false
      }
    },
    confirmDelete(product) {
      this.productPendingDelete = product
      this.deleteError = ""
      this.deleteDialogOpen = true
    },
    closeDeleteDialog() {
      if (this.deletingProduct) return
      this.deleteDialogOpen = false
      this.deleteError = ""
      this.productPendingDelete = null
    },
    async executeDelete() {
      if (!this.productPendingDelete || this.deletingProduct) return

      const product = this.productPendingDelete
      this.deletingProduct = true
      this.deleteError = ""
      try {
        await api.marketingShop.deleteProduct(product._id)
        this.deleteDialogOpen = false
        this.productPendingDelete = null
        this.$emit("reload")
        if (this.$toast) this.$toast.success(`„${product.title}“ wurde gelöscht`)
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
        this.deleteError = e.response?.data?.error || "Das Produkt konnte nicht gelöscht werden. Bitte versuche es erneut."
      } finally {
        this.deletingProduct = false
      }
    },
    markVariantFileForRemoval(sig) {
      if (!this.variantFilesToRemove.includes(sig)) {
        this.variantFilesToRemove.push(sig)
      }
    },
    getProductCombinations(product) {
      if (!product || !product.options || product.options.length === 0) return []
      const validOptions = product.options.filter((o) => o.name && o.name.trim() && o.choices && o.choices.some((c) => c.value && c.value.trim()))
      if (validOptions.length === 0) return []

      let results = [[]]
      for (const option of validOptions) {
        const nextResults = []
        const choices = option.choices.filter((c) => c.value && c.value.trim())
        for (const res of results) {
          for (const choice of choices) {
            nextResults.push([...res, choice.id])
          }
        }
        results = nextResults
      }

      return results.map((comb) => comb.sort().join(","))
    },
    getProductTotalStockInfo(product) {
      if (product.options && product.options.length > 0) {
        if (!product.variantStock || Object.keys(product.variantStock).length === 0) {
          return { type: "unlimited", text: "Unbegrenzt" }
        }

        let total = 0
        let hasUnlimited = false
        const combinations = this.getProductCombinations(product)
        if (combinations.length === 0) {
          return { type: "unlimited", text: "Unbegrenzt" }
        }

        for (const sig of combinations) {
          const val = product.variantStock[sig]
          if (val === undefined || val === null) {
            hasUnlimited = true
          } else {
            total += val
          }
        }

        if (hasUnlimited) {
          return { type: "unlimited", text: "Unbegrenzt (Varianten)" }
        }
        return { type: "limited", text: `${total} Stk gesamt`, value: total }
      } else {
        if (product.stock === undefined || product.stock === null) {
          return { type: "unlimited", text: "Unbegrenzt" }
        }
        return { type: "limited", text: `${product.stock} Stk`, value: product.stock }
      }
    },
  },
  beforeDestroy() {
    Object.values(this.imageUrls).forEach((url) => URL.revokeObjectURL(url))
  },
}
</script>

<style scoped>
.product-admin-workspace,
.product-dialog-card,
.delete-dialog-card {
  --admin-surface: rgba(255, 255, 255, 0.045);
  --admin-surface-strong: rgba(255, 255, 255, 0.075);
  --admin-border: rgba(255, 255, 255, 0.09);
  --admin-border-strong: rgba(255, 255, 255, 0.16);
  --admin-text-muted: rgba(255, 255, 255, 0.64);
  --admin-text-subtle: rgba(255, 255, 255, 0.48);
  --admin-green: #009640;
  --admin-green-soft: rgba(0, 150, 64, 0.14);
  color: #fff;
}

.product-admin-workspace {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.workspace-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.workspace-eyebrow,
.dialog-eyebrow {
  color: #75d99d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.workspace-title {
  margin: 4px 0 0;
  color: #fff;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  line-height: 1.2;
}

.workspace-subtitle {
  margin: 6px 0 0;
  color: var(--admin-text-muted);
  font-size: 14px;
}

.create-product-btn {
  min-height: 44px;
  flex-shrink: 0;
  padding-inline: 20px !important;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.kpi-card {
  min-height: 118px;
  background: var(--admin-surface) !important;
  border: 1px solid var(--admin-border) !important;
  border-radius: 14px !important;
}

.kpi-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  height: 100%;
  padding: 18px;
}

.kpi-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.kpi-label {
  color: var(--admin-text-muted);
  font-size: 12px;
  font-weight: 600;
}

.kpi-value {
  margin-top: 3px;
  color: #fff;
  font-size: 28px;
  line-height: 1.1;
}

.kpi-helper {
  margin-top: 7px;
  color: var(--admin-text-subtle);
  font-size: 11px;
}

.kpi-icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.kpi-icon--neutral {
  background: rgba(148, 163, 184, 0.14);
}

.kpi-icon--neutral .v-icon {
  color: #cbd5e1;
}

.kpi-icon--success {
  background: rgba(52, 211, 153, 0.14);
}

.kpi-icon--success .v-icon {
  color: #6ee7b7;
}

.kpi-icon--info {
  background: rgba(96, 165, 250, 0.14);
}

.kpi-icon--info .v-icon {
  color: #93c5fd;
}

.kpi-icon--warning {
  background: rgba(251, 191, 36, 0.15);
}

.kpi-icon--warning .v-icon {
  color: #fcd34d;
}

.filter-panel {
  margin-bottom: 16px;
  padding: 16px;
  background: var(--admin-surface) !important;
  border: 1px solid var(--admin-border) !important;
  border-radius: 14px !important;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(230px, 1.8fr) repeat(4, minmax(140px, 1fr)) auto;
  gap: 12px;
  align-items: center;
}

.filter-grid > *,
.filter-grid ::v-deep .v-input,
.filter-grid ::v-deep .v-input__control,
.filter-grid ::v-deep .v-input__slot {
  min-width: 0;
  max-width: 100%;
}

.reset-filter-btn {
  min-height: 44px;
  white-space: nowrap;
}

.filter-summary {
  display: flex;
  align-items: center;
  margin-top: 12px;
  color: var(--admin-text-muted);
  font-size: 12px;
}

.workspace-alert {
  margin-bottom: 16px;
}

.workspace-state {
  display: flex;
  min-height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: var(--admin-text-muted);
  text-align: center;
  background: rgba(255, 255, 255, 0.025);
  border: 1px dashed var(--admin-border-strong);
  border-radius: 14px;
}

.workspace-state-title {
  margin-top: 14px;
  color: #fff;
  font-size: 17px;
  font-weight: 650;
}

.workspace-state-copy {
  max-width: 420px;
  margin-top: 5px;
  font-size: 13px;
}

.product-table-card {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025) !important;
  border: 1px solid var(--admin-border) !important;
  border-radius: 14px !important;
}

.product-table {
  background: transparent !important;
}

.product-table-card ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.product-table-card ::v-deep tbody tr {
  transition: background-color 160ms ease-out;
}

.product-table-card ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.product-thumb {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--admin-border);
}

.table-product-title {
  max-width: 250px;
  overflow: hidden;
  color: #fff;
  font-size: 13.5px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-product-description {
  max-width: 250px;
  margin-top: 2px;
  overflow: hidden;
  color: var(--admin-text-subtle);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-secondary-text {
  color: var(--admin-text-muted);
  font-size: 12px;
}

.table-actions,
.mobile-card-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.icon-action {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
}

.mobile-product-list {
  display: grid;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.mobile-product-card {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 14px;
  background: var(--admin-surface);
  border: 1px solid var(--admin-border);
  border-radius: 14px;
}

.mobile-card-header {
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 12px;
}

.mobile-product-thumb {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--admin-border);
  border-radius: 10px;
}

.mobile-card-heading {
  width: 0;
  min-width: 0;
  flex: 1 1 0%;
  overflow: hidden;
}

.mobile-card-heading h3 {
  margin: 0;
  overflow: hidden;
  color: #fff;
  font-size: 16px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.mobile-product-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 0;
}

.mobile-product-meta > div {
  min-width: 0;
  padding: 10px;
  background: rgba(255, 255, 255, 0.035);
  border-radius: 9px;
}

.mobile-product-meta dt {
  color: var(--admin-text-subtle);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mobile-product-meta dd {
  margin: 4px 0 0;
  overflow: hidden;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-text--unlimited {
  color: #cbd5e1 !important;
}

.stock-text--out {
  color: #fca5a5 !important;
}

.stock-text--low {
  color: #fcd34d !important;
}

.stock-text--available {
  color: #6ee7b7 !important;
}

.mobile-card-actions {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--admin-border);
}

.mobile-edit-btn {
  min-height: 44px;
}

/* Dialog workspace */
.product-dialog-card {
  display: flex;
  max-height: min(90vh, 920px);
  flex-direction: column;
  overflow: hidden;
  background: #202124 !important;
}

.product-dialog-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px !important;
  background: #202124;
}

.dialog-title-copy {
  min-width: 0;
  flex: 1;
}

.dialog-title-copy h2,
.delete-dialog-title h2 {
  margin: 3px 0 0;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.dialog-title-copy p,
.delete-dialog-title p {
  margin: 4px 0 0;
  color: var(--admin-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.dialog-close-btn {
  margin: -4px -6px 0 0;
  flex: 0 0 44px;
}

.product-dialog-body {
  flex: 1 1 auto;
  padding: 20px 24px 28px !important;
  overflow-y: auto;
  background: #191a1c;
  scroll-behavior: smooth;
}

.form-section-map {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
}

.form-section-map li {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
  padding: 9px;
  color: var(--admin-text-muted);
  font-size: 10px;
  font-weight: 650;
  background: var(--admin-surface);
  border: 1px solid var(--admin-border);
  border-radius: 9px;
}

.map-number,
.section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 750;
  background: var(--admin-green);
  border-radius: 999px;
}

.map-number {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  font-size: 11px;
}

.form-error {
  margin-bottom: 16px;
}

.product-form-section {
  margin-bottom: 18px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--admin-border);
  border-radius: 14px;
}

.product-form-section:last-child {
  margin-bottom: 0;
}

.section-heading {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.025);
  border-bottom: 1px solid var(--admin-border);
}

.section-heading--with-action {
  grid-template-columns: 38px minmax(0, 1fr) auto;
}

.section-number {
  width: 34px;
  height: 34px;
  font-size: 14px;
}

.section-heading h3 {
  margin: 0;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}

.section-heading p {
  margin: 3px 0 0;
  color: var(--admin-text-muted);
  font-size: 11.5px;
  line-height: 1.45;
}

.section-action {
  min-height: 40px;
  align-self: center;
}

.section-body {
  padding: 18px;
}

.field-label {
  margin-bottom: 8px;
  color: var(--admin-text-muted);
  font-size: 12px;
  font-weight: 650;
}

.field-helper {
  display: block;
  margin: -10px 0 0 4px;
  color: var(--admin-text-subtle);
  font-size: 10.5px;
  line-height: 1.4;
}

.product-type-toggle .v-btn {
  min-height: 42px;
  padding-inline: 20px !important;
  text-transform: none;
}

.active-switch-column {
  padding-left: 18px !important;
}

.section-hint {
  margin: 8px 0 0;
}

.existing-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: 10px;
}

.existing-img-wrap {
  position: relative;
  overflow: hidden;
  aspect-ratio: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--admin-border);
  border-radius: 10px;
}

.existing-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.existing-img-loading {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.remove-img-btn {
  position: absolute !important;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.72) !important;
}

.option-card {
  margin-bottom: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.035) !important;
  border: 1px solid var(--admin-border) !important;
  border-radius: 11px !important;
}

.option-card:last-child {
  margin-bottom: 0;
}

.option-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: -6px -4px 6px 0;
}

.option-index {
  color: #75d99d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.choices-container {
  padding: 12px 0 0 14px;
  border-left: 2px solid rgba(0, 150, 64, 0.4);
}

.choice-row {
  display: grid;
  grid-template-columns: minmax(150px, 0.75fr) minmax(240px, 1.25fr) 44px;
  gap: 10px;
  align-items: start;
  margin-bottom: 4px;
}

.choice-remove-btn {
  margin-top: 2px;
}

.image-option-name {
  max-width: 250px;
}

.image-selection-name {
  max-width: 150px;
}

.add-choice-btn {
  min-height: 40px;
  margin-left: -12px;
}

.variants-empty-state {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px;
  color: var(--admin-text-muted);
  text-align: center;
  background: rgba(255, 255, 255, 0.018);
  border: 1px dashed var(--admin-border-strong);
  border-radius: 10px;
}

.variants-empty-state strong {
  margin-top: 9px;
  color: #fff;
  font-size: 13px;
}

.variants-empty-state span {
  max-width: 420px;
  margin-top: 3px;
  font-size: 11.5px;
}

.variant-stock-section {
  margin-top: 2px;
}

.subsection-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.subsection-heading > div {
  display: flex;
  flex-direction: column;
}

.subsection-heading strong {
  color: #fff;
  font-size: 13px;
}

.subsection-heading span {
  margin-top: 2px;
  color: var(--admin-text-muted);
  font-size: 11px;
}

.digital-file-section {
  margin-top: 18px;
  padding: 16px;
  background: var(--admin-green-soft);
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 11px;
}

.existing-file-card {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--admin-border);
  border-radius: 9px;
}

.existing-file-copy {
  min-width: 0;
  flex: 1;
}

.variant-file-label {
  margin-bottom: 6px;
  color: #fff;
  font-size: 12px;
  font-weight: 650;
}

.variant-file-name {
  max-width: 210px;
  font-size: 12px;
}

.product-dialog-footer {
  min-height: 76px;
  flex: 0 0 auto;
  padding: 12px 18px !important;
  background: #202124;
}

.footer-validation {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--admin-text-muted);
  font-size: 11px;
}

.footer-action {
  min-height: 44px;
}

.save-product-btn {
  padding-inline: 18px !important;
}

/* Delete dialog */
.delete-dialog-card {
  overflow: hidden;
  background: #202124 !important;
  border: 1px solid var(--admin-border);
  border-radius: 14px !important;
}

.delete-dialog-title {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 22px 12px !important;
}

.delete-dialog-title h2 {
  font-size: 19px;
}

.delete-icon {
  display: flex;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  border-radius: 12px;
}

.delete-product-name {
  margin-bottom: 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.delete-dialog-actions {
  padding: 12px 18px 18px !important;
}

.product-admin-workspace .v-btn:focus-visible,
.product-dialog-card .v-btn:focus-visible,
.delete-dialog-card .v-btn:focus-visible {
  outline: 3px solid rgba(117, 217, 157, 0.75);
  outline-offset: 2px;
}

@media (max-width: 1260px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-grid > :first-child {
    grid-column: span 2;
  }
}

@media (max-width: 960px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .workspace-header {
    flex-direction: column;
    gap: 14px;
    margin-bottom: 14px;
  }

  .workspace-subtitle {
    font-size: 12.5px;
  }

  .create-product-btn {
    width: 100%;
  }

  .kpi-grid {
    min-width: 0;
    gap: 6px;
    margin-bottom: 12px;
  }

  .kpi-card {
    min-width: 0;
    min-height: 86px;
    box-sizing: border-box;
    border-radius: 11px !important;
  }

  .kpi-content {
    min-width: 0;
    gap: 8px;
    padding: 10px;
  }

  .kpi-copy {
    overflow: hidden;
  }

  .kpi-label {
    overflow: hidden;
    font-size: 10.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .kpi-value {
    margin-top: 1px;
    font-size: 22px;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
    border-radius: 9px;
  }

  .kpi-icon .v-icon {
    font-size: 19px !important;
  }

  .kpi-helper {
    margin-top: 3px;
    overflow: hidden;
    font-size: 10.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filter-panel {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    margin-bottom: 12px;
    padding: 10px;
    border-radius: 11px !important;
  }

  .filter-grid {
    min-width: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .filter-grid > :first-child {
    grid-column: 1 / -1;
  }

  .filter-grid > * {
    width: 100%;
    min-width: 0;
  }

  .filter-summary {
    min-width: 0;
    margin-top: 8px;
    overflow: hidden;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .reset-filter-btn {
    width: 100%;
    min-height: 44px;
    grid-column: 1 / -1;
  }

  .mobile-product-list {
    gap: 10px;
  }

  .mobile-product-card {
    padding: 12px;
    border-radius: 11px;
  }

  .mobile-card-header {
    gap: 10px;
  }

  .mobile-product-thumb {
    width: 56px;
    height: 56px;
    flex-basis: 56px;
    border-radius: 9px;
  }

  .mobile-card-heading h3 {
    font-size: 15px;
  }

  .mobile-card-chips {
    gap: 4px;
    margin-top: 5px;
  }

  .mobile-product-meta {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    margin-top: 12px;
  }

  .mobile-product-meta > div {
    min-width: 0;
    padding: 8px;
  }

  .mobile-product-meta > div:last-child {
    display: flex;
    min-height: 38px;
    grid-column: 1 / -1;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .mobile-product-meta dt {
    font-size: 10.5px;
  }

  .mobile-product-meta dd {
    max-width: 100%;
    margin-top: 3px;
    overflow: visible;
    line-height: 1.35;
    overflow-wrap: anywhere;
    text-overflow: clip;
    white-space: normal;
  }

  .mobile-product-meta > div:last-child dd {
    margin-top: 0;
    text-align: right;
  }

  .mobile-card-actions {
    flex-wrap: nowrap;
    gap: 4px;
    margin-top: 8px;
    padding-top: 6px;
  }

  .mobile-card-actions > .v-spacer {
    min-width: 0;
  }

  .mobile-edit-btn {
    min-width: 0 !important;
    margin-left: auto;
    padding-inline: 10px !important;
  }

  .product-dialog-card {
    max-height: 100vh;
    height: 100%;
    border-radius: 0 !important;
  }

  .product-dialog-header {
    padding: 16px !important;
  }

  .dialog-title-copy h2 {
    font-size: 19px;
  }

  .dialog-title-copy p {
    display: none;
  }

  .product-dialog-body {
    padding: 14px 12px 22px !important;
  }

  .form-section-map {
    display: flex;
    margin-bottom: 14px;
    overflow-x: auto;
    scrollbar-width: thin;
  }

  .form-section-map li {
    min-width: max-content;
  }

  .section-heading,
  .section-heading--with-action {
    grid-template-columns: 34px minmax(0, 1fr);
    padding: 14px;
  }

  .section-action {
    grid-column: 1 / -1;
    width: 100%;
    margin-top: 2px;
  }

  .section-body {
    padding: 14px;
  }

  .active-switch-column {
    padding-left: 4px !important;
  }

  .choice-row {
    grid-template-columns: minmax(0, 1fr) 44px;
    gap: 8px;
  }

  .choice-value,
  .choice-image-select {
    grid-column: 1 / -1;
  }

  .choice-remove-btn {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  .choices-container {
    padding-left: 10px;
  }

  .subsection-heading {
    align-items: center;
  }

  .product-dialog-footer {
    flex-wrap: wrap;
    min-height: 94px;
    padding: 10px 12px !important;
  }

  .footer-validation {
    width: 100%;
    order: -1;
  }

  .product-dialog-footer .v-spacer {
    display: none;
  }

  .footer-action {
    flex: 1 1 auto;
  }

  .delete-dialog-actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 360px) {
  .filter-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .filter-grid > :first-child,
  .reset-filter-btn {
    grid-column: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .product-admin-workspace *,
  .product-dialog-card *,
  .delete-dialog-card * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
